import React, { useState } from "react";
import Popup from "reactjs-popup";
import Flatpickr from "react-flatpickr";
import { offsetDateForBrowser, offsetDateForServer } from "../../../shared/timeZoneLogic";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { useSeriesShowContext } from "../SeriesShowApp";
import { Button } from "@opendate-app/setlist";

const ACTIVITIES = [
  { key: "announce_time", label: "Announce" },
  { key: "public_on_sale_time", label: "Public On-Sale" },
  { key: "venue_pre_sale_time", label: "Venue Pre-Sale" },
  { key: "artist_pre_sale_time", label: "Artist Pre-Sale" },
];

const formatTime = (value) => {
  if (!value) return "Not set";
  return new Date(value).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
};

const CONFIRM_POPUP_STYLE = {
  border: "none",
  borderRadius: 8,
  padding: 0,
  width: "min(440px, calc(100vw - 40px))",
  textAlign: "left",
};

const ShowActivitiesCard = () => {
  const { series, handleUpdateShowActivity } = useSeriesShowContext();
  const times = series.effective_show_activity_times || {};
  const eventCount = series.summary_stats?.events_in_series?.total;
  const lockMessage = series.sale_times_lock_message;

  const [editingKey, setEditingKey] = useState(null);
  const [draftDate, setDraftDate] = useState(null);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  const beginEdit = (activity) => {
    setEditingKey(activity.key);
    setDraftDate(offsetDateForBrowser(times[activity.key], series.venue_time_zone) || null);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraftDate(null);
  };

  const requestSave = (activity) => {
    if (!draftDate) return;
    setPending({ ...activity, date: draftDate });
  };

  const applyChange = async () => {
    if (!pending || saving) return;
    try {
      setSaving(true);
      await handleUpdateShowActivity(pending.key, offsetDateForServer(pending.date, series.venue_time_zone).toISOString());
      toastSuccess("Updated every event in the series.");
      setPending(null);
      cancelEdit();
    } catch (e) {
      toastError(e?.response?.data?.errors?.[0] || "Could not update the series. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const eventsPhrase = eventCount != null
    ? `${eventCount} ${eventCount === 1 ? "event" : "events"} in this series`
    : "every event in this series";

  return (
    <div className="series-detail-card">
      <div className="series-detail-card-body">
        <h4 className="series-detail-card-title mb-1">Show Activities</h4>
        <p className="text-muted series-detail-card-subtext mb-3">Default show flow for all events.</p>
        {lockMessage && <p className="series-detail-card-subtext mb-3" data-testid="sale-times-lock">{lockMessage}</p>}
        <table className="table mb-0 series-activities-table">
          <thead>
            <tr>
              <th>Activity</th>
              <th>Time</th>
              <th aria-label="Edit"></th>
            </tr>
          </thead>
          <tbody>
            {ACTIVITIES.map((activity) => {
              const isEditing = editingKey === activity.key;
              return (
                <tr key={activity.key}>
                  <td className="series-activities-name">
                    <i className="fal fa-lock-alt series-activities-lock" />
                    {activity.label}
                  </td>
                  <td className={times[activity.key] || isEditing ? "series-activities-time" : "series-activities-time text-muted"}>
                    {isEditing ? (
                      <div className="flatpickr-date-input px-2 d-flex align-items-center" style={{ maxWidth: 240 }}>
                        <i className="far fa-clock pr-2 text-muted" />
                        <Flatpickr
                          options={{ enableTime: true, dateFormat: "M j, Y h:i K" }}
                          value={draftDate || ""}
                          placeholder="Select date / time"
                          onChange={([d]) => setDraftDate(d)}
                        />
                      </div>
                    ) : (
                      formatTime(offsetDateForBrowser(times[activity.key], series.venue_time_zone))
                    )}
                  </td>
                  <td className="series-activities-edit text-right text-nowrap">
                    {isEditing ? (
                      <>
                        <Button variant="secondary" size="sm" onClick={cancelEdit}>
                          Cancel
                        </Button>
                        <Button variant="primary" size="sm" onClick={() => requestSave(activity)} disabled={!draftDate}>
                          Save
                        </Button>
                      </>
                    ) : !lockMessage && (
                      <button type="button" className="series-activities-edit-btn" onClick={() => beginEdit(activity)} aria-label={`Edit ${activity.label}`}>
                        <i className="far fa-pencil" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Popup open={Boolean(pending)} modal className="popup-modal" contentStyle={CONFIRM_POPUP_STYLE} onClose={() => !saving && setPending(null)} closeOnDocumentClick={!saving}>
        <div className="popup-modal-container">
          <div className="row m-0 series-confirm-header">
            <div className="col series-confirm-header-col">
              <p className="mb-0 series-confirm-title"><strong>Update {pending && pending.label}?</strong></p>
            </div>
          </div>
          <div className="row m-0">
            <div className="col-12 series-confirm-body">
              <p className="text-muted mb-4">
                This sets <strong>{pending && pending.label}</strong> to <strong>{pending && formatTime(pending.date)}</strong> for{" "}
                <strong>{eventsPhrase}</strong>, including events that already have tickets sold.
              </p>
              <div className="d-flex justify-content-end series-confirm-actions">
                <Button variant="secondary" onClick={() => setPending(null)} disabled={saving}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={applyChange} disabled={saving}>
                  {saving ? "Updating…" : "Update all events"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default ShowActivitiesCard;
